<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useAuthStore } from '@/stores/auth.ts';
import UserDropdown from '@/components/shared/UserDropdown.vue';
import { useRouter } from 'vue-router';

const authStore = useAuthStore();
const router = useRouter();

const isLoginPage = computed(() => [
  '/login',
  '/confirm-email',
  '/register',
  '/sign-out-success',
].includes(router.currentRoute.value.path));

const isMenuOpen = ref(false);
const isScrolled = ref(false);
const blogDropdownOpen = ref(false);

const blogPosts = [
  { path: '/blog/the-new-workplace-rules-what-employees-need-to-know-in-2027', title: 'The New Workplace Rules: What Employees Need to Know in 2027' },
  { path: '/blog/using-ai-at-work-whats-helpful-whats-risky-and-what-to-avoid', title: 'Using AI at Work: What’s Helpful, What’s Risky, and What to Avoid' },
  { path: '/blog/ten-practical-tips-for-a-stress-free-first-day-at-a-new-job', title: '10 Practical Tips for a Stress-Free First Day at a New Job' },
  { path: '/blog/the-ultimate-onboarding-checklist-for-employers', title: 'The Ultimate Onboarding Checklist for Employers' },
  { path: '/blog/how-to-make-a-great-first-impression', title: 'How to Make a Great First Impression' },
  { path: '/blog/how-to-build-strong-relationships-with-your-new-colleagues', title: 'How to Build Strong Relationships with Your New Colleagues' },
  { path: '/blog/from-new-hire-to-high-performer-how-to-stand-out-in-your-first-year', title: 'From New Hire to High Performer: How to Stand Out in Your First Year' },
  { path: '/blog/navigating-office-politics-a-guide-for-new-employees', title: 'Navigating Office Politics: A Guide for New Employees' },
  { path: '/blog/time-management-hacks-for-new-employees', title: 'Time Management Hacks for New Employees' },
  { path: '/blog/skills-every-new-employee-should-develop-this-year', title: 'Skills Every New Employee Should Develop This Year' },
];

const mainNav = computed(() => {
  const items = [
    { path: '/', title: 'Home' },
    { path: '/about', title: 'About' },
    { path: '/features', title: 'Features' },
    { path: '/pricing', title: 'Pricing' },
    { type: 'dropdown', title: 'Blog', items: blogPosts },
  ];

  if (isAuthenticated.value) {
    items.unshift({ path: '/dashboard', title: 'Dashboard' });
  }

  return items;
});

const isAuthenticated = computed(() => authStore.isAuthenticated);
const isInitialized = computed(() => authStore.isInitialized);

const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value;
};

const closeMenu = () => {
  isMenuOpen.value = false;
  blogDropdownOpen.value = false;
};

const toggleBlogDropdown = (event) => {
  event.preventDefault();
  blogDropdownOpen.value = !blogDropdownOpen.value;
};

const handleScroll = () => {
  // Add a small debounce to improve performance
  if (!scrolling) {
    window.requestAnimationFrame(() => {
      const scrolled = window.scrollY > 20;
      isScrolled.value = scrolled;

      // Toggle 'scrolled' class on body
      if (scrolled) {
        document.body.classList.add('scrolled');
      } else {
        document.body.classList.remove('scrolled');
      }

      scrolling = false;
    });
    scrolling = true;
  }
};

let scrolling = false;

onMounted(() => {
  window.addEventListener('scroll', handleScroll, { passive: true });
  // Initial check
  handleScroll();

  // Ensure body padding is set correctly on initial load
  if (window.scrollY > 20) {
    document.body.classList.add('scrolled');
  }
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
});
</script>

<template>
  <header class="fixed top-0 left-0 w-full bg-white border-b border-gray-200 z-50 transition-all duration-300" :class="{ 'shadow-sm': isScrolled, 'h-24': isScrolled, 'h-32': !isScrolled }">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
      <div class="flex justify-between items-center h-full transition-all duration-300">
        <router-link to="/" class="flex items-center">
          <img
            src="@/assets/images/logo-integrate-me.png"
            alt="IntegrateMe"
            class="transition-all duration-300 object-contain position-relative z-index--2"
            :class="isScrolled ? 'h-[60px]' : 'h-[90px]'"
          />
        </router-link>
        <button
          class="navbar-burger"
          :class="{ 'is-active': isMenuOpen }"
          @click="toggleMenu"
          aria-label="Toggle navigation"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
        <div class="navbar-menu" :class="{ 'is-active': isMenuOpen }">
          <nav v-if="isInitialized" class="md:flex items-center space-x-8">
            <template v-for="item in mainNav" :key="item.path || item.title">
              <!-- Regular nav item -->
              <router-link
                v-if="!item.type"
                :to="item.path"
                class="navbar-item transition-colors duration-200"
                active-class="text-primary-600 font-semibold"
                @click="closeMenu"
              >
                {{ item.title }}
              </router-link>

              <!-- Dropdown nav item -->
              <div v-else-if="item.type === 'dropdown'" class="navbar-item has-dropdown relative">
                <a
                  href="#"
                  class="navbar-item transition-colors duration-200 flex items-center"
                  :class="{ 'text-primary-600 font-semibold': blogDropdownOpen }"
                  @click="toggleBlogDropdown"
                >
                  {{ item.title }}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="dropdown-icon"
                    :class="{ 'dropdown-icon-open': blogDropdownOpen }"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    width="24"
                    height="24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </a>
                <div
                  class="navbar-dropdown"
                  :class="{ 'block': blogDropdownOpen }"
                >
                  <router-link
                    v-for="subItem in item.items"
                    :key="subItem.path"
                    :to="subItem.path"
                    class="navbar-item transition-colors duration-200"
                    @click="closeMenu"
                  >
                    {{ subItem.title }}
                  </router-link>
                </div>
              </div>
            </template>
          </nav>
          <div class="navbar-end">
            <template v-if="isAuthenticated">
              <UserDropdown
                :show-dashboard="false"
                @close="closeMenu"
              />
            </template>
            <template v-else>
              <div class="navbar-item">
                <router-link
                  v-if="!isLoginPage"
                  to="/login"
                  class="btn secondary-btn secondary-btn--sm mr-2"
                  @click="closeMenu"
                >
                  Login
                </router-link>
                <router-link
                  v-if="!isLoginPage"
                  to="/register"
                  class="btn primary-btn primary-btn--sm"
                  @click="closeMenu"
                >
                  Register
                </router-link>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<style scoped>
/* Navbar container */
header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background-color: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: height, box-shadow, transform;
  transform: translateZ(0);
  backface-visibility: hidden;
  -webkit-font-smoothing: subpixel-antialiased;
}

/* Ensure content doesn't jump when navbar changes size */
body {
  padding-top: 128px; /* Default height when not scrolled (h-32) */
  transition: padding-top 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

body.scrolled {
  padding-top: 96px; /* Height when scrolled (h-24) */
}

/* Logo transition */
img {
  transition: height 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.navbar.is-scrolled {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  background-color: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.navbar-brand {
  display: flex;
  align-items: center;
  height: 100%;
  position: relative;
  z-index: 1;
}

.navbar-logo {
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0.75rem 0;
}

.logo {
  max-height: 90px;
  width: auto;
}

.navbar-burger {
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.75rem;
  margin-left: 0.5rem;
  position: relative;
  z-index: 1001;
}

.navbar-burger span {
  display: block;
  width: 20px;
  height: 2px;
  background-color: var(--text);
  margin: 4px 0;
  transition: all 0.3s ease;
}

.navbar-burger.is-active span:nth-child(1) {
  transform: translateY(6px) rotate(45deg);
}

.navbar-burger.is-active span:nth-child(2) {
  opacity: 0;
}

.navbar-burger.is-active span:nth-child(3) {
  transform: translateY(-6px) rotate(-45deg);
}

.navbar-menu {
  display: flex;
  flex-grow: 1;
  justify-content: space-between;
  align-items: center;
}

.navbar-start,
.navbar-end {
  display: flex;
  align-items: center;
}

.navbar-item {
  padding: 0.5rem 1rem;
  color: var(--text);
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;
  display: inline-flex;
  align-items: center;
  border-radius: 4px;
  margin: 0 0.25rem;
  position: relative;
}

.navbar-item.has-dropdown {
  padding: 0;
  margin: 0;
}

.navbar-item.is-active {
  color: var(--primary);
  font-weight: 600;
}

/* User menu */
.user-menu-trigger {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 0.5rem 0.75rem;
  border-radius: 50px;
  transition: background-color 0.2s ease;
}

.user-menu-trigger:hover {
  background-color: var(--gray-100);
}

.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: var(--primary);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.875rem;
  margin-right: 0.5rem;
  overflow: hidden;
}

.user-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.user-name {
  margin: 0 0.5rem 0 0.25rem;
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.navbar-dropdown {
  display: none;
  position: absolute;
  top: 100%;
  right: 0;
  min-width: 310px;
  max-width: calc(100vw - 2rem);
  max-height: calc(100vh - 128px - 1.5rem);
  overflow-y: auto;
  background: var(--white);
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  padding: 0.5rem 0;
  z-index: 1000;
  border: 1px solid var(--border);
}

.navbar-dropdown.block {
  display: block;
}

.navbar-dropdown .navbar-item {
  padding: 0.5rem 1.5rem;
  margin: 0;
  border-radius: 0;
  display: flex;
  align-items: center;
  width: 100%;
}

.navbar-dropdown .navbar-item .icon {
  margin-right: 0.75rem;
  width: 1.25rem;
  text-align: center;
}

.navbar-divider {
  height: 1px;
  background-color: var(--border);
  margin: 0.5rem 0;
  border: none;
}

/* Mobile styles */
@media (max-width: 1023px) {
  .navbar-burger {
    display: block;
  }

  .navbar-menu {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: var(--white);
    flex-direction: column;
    justify-content: flex-start;
    padding: 5rem 2rem 2rem;
    transform: translateY(-100%);
    opacity: 0;
    visibility: hidden;
    transition: all 0.3s ease;
    overflow-y: auto;
  }

  .navbar-menu.is-active {
    transform: translateY(0);
    opacity: 1;
    visibility: visible;
    height: calc(100vh);
  }

  .navbar-start,
  .navbar-end {
    flex-direction: column;
    width: 100%;
    margin-bottom: 2rem;
  }

  .navbar-item {
    width: 100%;
    padding: 0.75rem 0;
    margin: 0.25rem 0;
    justify-content: center;
  }

  .navbar-item.has-dropdown {
    flex-direction: column;
  }

  .navbar-dropdown {
    position: static;
    display: none;
    box-shadow: none;
    background: transparent;
    padding: 0;
    margin-top: 0.5rem;
    border: none;
    max-width: 100%;
    max-height: 40vh;
    text-align: center;
  }
}
/* Dropdown icon */
.dropdown-icon {
  height: 16px;
  width: 16px;
  margin-left: 4px;
  transition: transform 0.2s ease;
}

.dropdown-icon-open {
  transform: rotate(180deg);
}
</style>
